'use client'

import { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'

const MONTHS_FR = [
  'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
  'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
]
const DAYS_FR = ['L', 'M', 'M', 'J', 'V', 'S', 'D']

function daysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate()
}
function firstWeekday(year: number, month: number) {
  const d = new Date(year, month, 1).getDay()
  return d === 0 ? 6 : d - 1 // Mon=0 … Sun=6
}
function parseDate(v?: string): Date | null {
  if (!v) return null
  const d = new Date(v)
  return isNaN(d.getTime()) ? null : d
}
function formatDisplay(d: Date) {
  return (
    d.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' }) +
    ' à ' +
    d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  )
}

interface Props {
  name: string
  defaultValue?: string
  required?: boolean
  placeholder?: string
  align?: 'left' | 'right'
}

export function DateTimePicker({ name, defaultValue, required, placeholder = 'Choisir une date…', align = 'left' }: Props) {
  const initial = parseDate(defaultValue)
  const [selected, setSelected] = useState<Date | null>(initial)
  const [draft, setDraft] = useState<Date>(initial ?? roundToNext5(new Date()))
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(draft.getFullYear())
  const [viewMonth, setViewMonth] = useState(draft.getMonth())
  const containerRef = useRef<HTMLDivElement>(null)
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({})
  const [mounted, setMounted] = useState(false)

  useEffect(() => { setMounted(true) }, [])

  // Ferme sur clic extérieur (le popover stopPropagation sur mousedown pour s'exclure)
  useEffect(() => {
    function onDown(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [])

  const prevMonth = useCallback(() => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear((y) => y - 1) }
    else setViewMonth((m) => m - 1)
  }, [viewMonth])

  const nextMonth = useCallback(() => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear((y) => y + 1) }
    else setViewMonth((m) => m + 1)
  }, [viewMonth])

  function pickDay(day: number) {
    const d = new Date(draft)
    d.setFullYear(viewYear, viewMonth, day)
    setDraft(d)
  }

  function nudgeHours(delta: number) {
    const d = new Date(draft)
    d.setHours(((d.getHours() + delta) % 24 + 24) % 24)
    setDraft(d)
  }

  function nudgeMinutes(delta: number) {
    const d = new Date(draft)
    const cur = Math.round(d.getMinutes() / 5) * 5
    d.setMinutes(((cur + delta) % 60 + 60) % 60)
    setDraft(d)
  }

  function confirm() {
    setSelected(new Date(draft))
    setOpen(false)
  }

  function openPicker() {
    if (!open) {
      const base = selected ?? roundToNext5(new Date())
      setDraft(new Date(base))
      setViewYear(base.getFullYear())
      setViewMonth(base.getMonth())
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect()
        const spaceBelow = window.innerHeight - rect.bottom
        const popoverHeight = 280
        setPopoverStyle(
          spaceBelow >= popoverHeight || spaceBelow >= rect.top
            ? { position: 'fixed', top: rect.bottom + 8, left: align === 'right' ? rect.right - 340 : rect.left, minWidth: 340, zIndex: 9999 }
            : { position: 'fixed', bottom: window.innerHeight - rect.top + 8, left: align === 'right' ? rect.right - 340 : rect.left, minWidth: 340, zIndex: 9999 }
        )
      }
    }
    setOpen((v) => !v)
  }

  const today = new Date()
  const totalDays = daysInMonth(viewYear, viewMonth)
  const offset = firstWeekday(viewYear, viewMonth)

  const isToday = (d: number) =>
    today.getFullYear() === viewYear && today.getMonth() === viewMonth && today.getDate() === d
  const isDraft = (d: number) =>
    draft.getFullYear() === viewYear && draft.getMonth() === viewMonth && draft.getDate() === d

  return (
    <div ref={containerRef} className="relative">
      {/* Hidden value for FormData */}
      <input type="hidden" name={name} value={selected ? selected.toISOString() : ''} />

      {/* Trigger */}
      <button
        type="button"
        onClick={openPicker}
        className={`glass-input w-full text-left flex items-center gap-2.5 cursor-pointer select-none ${
          selected ? 'text-text-primary' : 'text-text-muted'
        }`}
        aria-expanded={open}
      >
        <svg className="w-4 h-4 flex-shrink-0 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
        </svg>
        <span className="flex-1 truncate text-sm">
          {selected ? formatDisplay(selected) : placeholder}
        </span>
        <svg className={`w-3.5 h-3.5 text-text-muted transition-transform flex-shrink-0 ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Popover — portal pour passer au-dessus des backdrop-filter parents */}
      {open && mounted && createPortal(
        <div
          onMouseDown={(e) => e.stopPropagation()}
          className="glass-card border border-white/[0.15] rounded-2xl shadow-2xl p-4 flex gap-4"
          style={{
            ...popoverStyle,
            background: 'rgba(10,10,20,0.92)',
            backdropFilter: 'blur(24px) saturate(160%)',
          }}
        >
          {/* ─ Calendrier ─ */}
          <div className="flex-1 min-w-0">
            {/* Navigation mois */}
            <div className="flex items-center justify-between mb-3">
              <button
                type="button"
                onClick={prevMonth}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-text-muted hover:text-text-primary hover:bg-white/[0.08] transition-colors text-base"
              >
                ‹
              </button>
              <span className="text-text-primary text-sm font-semibold capitalize">
                {MONTHS_FR[viewMonth]} {viewYear}
              </span>
              <button
                type="button"
                onClick={nextMonth}
                className="w-7 h-7 flex items-center justify-center rounded-lg text-text-muted hover:text-text-primary hover:bg-white/[0.08] transition-colors text-base"
              >
                ›
              </button>
            </div>

            {/* En-têtes jours */}
            <div className="grid grid-cols-7 mb-1">
              {DAYS_FR.map((d, i) => (
                <div key={i} className="text-center text-text-muted text-[10px] font-medium py-0.5">{d}</div>
              ))}
            </div>

            {/* Grille jours */}
            <div className="grid grid-cols-7 gap-0.5">
              {Array.from({ length: offset }).map((_, i) => <div key={`_${i}`} />)}
              {Array.from({ length: totalDays }).map((_, i) => {
                const day = i + 1
                const sel = isDraft(day)
                const tod = isToday(day)
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => pickDay(day)}
                    className={[
                      'w-8 h-8 mx-auto flex items-center justify-center rounded-lg text-xs transition-all font-medium',
                      sel
                        ? 'bg-accent text-white shadow-lg shadow-accent/30'
                        : tod
                        ? 'border border-accent/40 text-accent hover:bg-accent/10'
                        : 'text-text-secondary hover:bg-white/[0.08] hover:text-text-primary',
                    ].join(' ')}
                  >
                    {day}
                  </button>
                )
              })}
            </div>
          </div>

          {/* ─ Sélecteur heure ─ */}
          <div className="flex flex-col items-center gap-2 border-l border-white/[0.08] pl-4 py-1">
            <p className="text-text-muted text-[10px] uppercase tracking-wider font-medium mb-1">Heure</p>

            {/* Heures */}
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={() => nudgeHours(1)}
                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-white/[0.08] rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                </svg>
              </button>
              <span className="text-text-primary text-2xl font-bold w-10 text-center tabular-nums leading-none">
                {String(draft.getHours()).padStart(2, '0')}
              </span>
              <button
                type="button"
                onClick={() => nudgeHours(-1)}
                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-white/[0.08] rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
            </div>

            <span className="text-text-primary text-2xl font-bold leading-none select-none">:</span>

            {/* Minutes */}
            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={() => nudgeMinutes(5)}
                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-white/[0.08] rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" />
                </svg>
              </button>
              <span className="text-text-primary text-2xl font-bold w-10 text-center tabular-nums leading-none">
                {String(Math.round(draft.getMinutes() / 5) * 5 % 60).padStart(2, '0')}
              </span>
              <button
                type="button"
                onClick={() => nudgeMinutes(-5)}
                className="w-8 h-8 flex items-center justify-center text-text-muted hover:text-text-primary hover:bg-white/[0.08] rounded-lg transition-colors"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>
            </div>

            {/* Confirmer */}
            <button
              type="button"
              onClick={confirm}
              className="mt-3 px-4 py-2 text-xs font-semibold rounded-xl transition-all"
              style={{
                background: 'rgba(110,127,255,0.25)',
                border: '1px solid rgba(110,127,255,0.4)',
                color: '#8B9AFF',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(110,127,255,0.4)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(110,127,255,0.25)'
              }}
            >
              Confirmer
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

function roundToNext5(d: Date) {
  const r = new Date(d)
  r.setSeconds(0, 0)
  r.setMinutes(Math.ceil(r.getMinutes() / 5) * 5)
  return r
}
